import React, { ReactElement, useState } from 'react';
import {
  Button, Col, Heading, Row, LoaderButton,
} from 'idsk-react-components';
import { useLocation, useNavigate, useParams } from 'react-router';
import './vyhodnotenieForm.scss';
import VyhodnotenieSelectBox from './component/VyhodnotenieSelectBox';
import ClassType from '../../model/pripomienka/ClassType';
import { SafeParameterSetter } from '../../types/SafeParameterSetter';
import AddVyhodnotenie from '../../model/pripomienka/AddVyhodnotenie';
import { PodporovatelList } from '../PodporovatelList';
import { useSuccessMessageContext } from '../../context/SuccessMessageContext';
import { useClient } from '../../context/ClientStatusContext';
import { addVyhodnotenie } from '../../client/addVyhodnotenieClient';
import useFieldErrorMessages from '../useFieldErrorMessages';
import PripomienkaDetail from '../../model/pripomienka/PripomienkaDetail';
import VyhodnotenieText from './component/VyhodnotenieText';
import PripomienkaDetailRow from '../PripomienkaDetail/component/PripomienkaDetailRow';
import routes from '../../routing/routes';
import TypPripomienky from '../../model/pripomienka/TypPripomienky';
import Uuid from '../../model/Uuid';
import { useHistoryContext } from '../../context/HistoryContext';
import { useErrorMessageContext } from '../../context/ErrorMessageContext';

interface Props {
  classType: ClassType;
  pripomienkaDetail: PripomienkaDetail;
  getBackUrl: () => string;
}

const VyhodnotenieForm = ({
  classType, pripomienkaDetail, getBackUrl,
}: Props): ReactElement => {
  const {
    type,
    year,
    index,
    uuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, uuid: string }>();
  const { lastVisitedListPage } = useHistoryContext();

  const getNextUrl = (nextPripomienkaId: Uuid): string => (pripomienkaDetail.typ === TypPripomienky.HROMADNA
    ? routes.hromadnePripomienky.evaluate.withParams(type, year, index, nextPripomienkaId)
    : routes.pripomienky.evaluate.withParams(type, year, index, nextPripomienkaId));

  const navigate = useNavigate();
  const location = useLocation();
  const { displaySuccess } = useSuccessMessageContext();
  const { displayError } = useErrorMessageContext();

  const [vyhodnotenie, setVyhodnotenie] = useState<AddVyhodnotenie>();

  const { status, wrapClient: saveClient } = useClient(addVyhodnotenie);
  const { clearFieldErrorMessage } = useFieldErrorMessages();

  const updateField: SafeParameterSetter<AddVyhodnotenie> = (fieldName, value) => {
    setVyhodnotenie(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const handleSubmit = async (e): Promise<void> => {
    e.preventDefault();
    await saveClient(
      classType,
      type,
      year,
      index,
      uuid,
      vyhodnotenie,
    );
    navigate(lastVisitedListPage || getBackUrl());
    displaySuccess('Vyhodnotenie pripomienky bolo úspešne uložené.');
  };

  const handleClick = async (e): Promise<void> => {
    e.preventDefault();
    await saveClient(
      classType,
      type,
      year,
      index,
      uuid,
      vyhodnotenie,
    ).then(response => {
      if (response.data !== null) {
        navigate(getNextUrl(response.data), { replace: true });
      } else {
        displayError('Všetky pripomienky boli spracované.');
        window.parent.postMessage({
          type: 'scroll-to',
          top: 0,
        }, '*');
      }
      displaySuccess('Vyhodnotenie pripomienky bolo úspešne uložené.');
    });
  };

  return pripomienkaDetail && (
  <section className="vyhodnotenie-form-section">
    <Row>
      <Col>
        <Heading size="m">VYHODNOTENIE</Heading>
      </Col>
    </Row>
    <PripomienkaDetailRow
      header="Priradená:"
      data={pripomienkaDetail.priradeny ? pripomienkaDetail.priradeny.fullName : null}
    />
    <PripomienkaDetailRow
      header="Vyhodnotil používateľ:"
      data={pripomienkaDetail.vyhodnotil ? pripomienkaDetail.vyhodnotil.fullName : null}
    />
    <form>
      <VyhodnotenieSelectBox stavFromDetail={pripomienkaDetail.stav} updateField={updateField} />
      <VyhodnotenieText
        textFromDetail={pripomienkaDetail.textVyhodnotenia}
        updateField={updateField}
      />
      {classType === ClassType.HROMADNA_PRIPOMIENKA && (<PodporovatelList />)}
      <div className="button-wrapper">
        <LoaderButton
          loading={status.loading}
          type="submit"
          className="save-vyhodnotenie-button"
          onClick={handleSubmit}
          size="large"
        >Uložiť
        </LoaderButton>
        <LoaderButton
          loading={status.loading}
          type="button"
          className="save-vyhodnotenie-button"
          onClick={handleClick}
          size="large"
        >Uložiť a prejsť na ďalšiu
        </LoaderButton>
        <Button
          buttonStyle="secondary"
          size="large"
          type="reset"
          onClick={() => {
            const hasPreviousState = location.key !== 'default';
            if (hasPreviousState) {
              navigate(-1);
            } else {
              navigate(getBackUrl());
            }
          }}
        >
          Zrušiť
        </Button>
      </div>
    </form>
  </section>
  );
};

export default VyhodnotenieForm;
